/**
 * Collection Sort Dropdown – custom element for collection toolbar sorting.
 * Sorts the product grid in the DOM (main-collection-product-grid) by the selected option.
 * Product items must have data-product-title, data-product-price, data-product-created.
 */
(function () {
  const GRID_SELECTOR = "#main-collection-product-grid";
  const ITEM_SELECTOR = ".product-item";
  const DEFAULT_LABEL = "Sort By";

  const SORT_OPTIONS = [
    { value: "title-ascending", label: "A-Z" },
    { value: "title-descending", label: "Z-A" },
    { value: "price-ascending", label: "Price, Low to High" },
    { value: "price-descending", label: "Price, High to Low" },
    { value: "created-ascending", label: "Old to New" },
    { value: "created-descending", label: "New to Old" },
  ];

  class CollectionSortDropdown extends HTMLElement {
    constructor() {
      super();
      this._open = false;
      this._currentValue = this.getAttribute("value") || "";
      this.boundCloseOnClickOutside = this.closeOnClickOutside.bind(this);
      this.boundCloseOnEscape = this.closeOnEscape.bind(this);
    }

    static get observedAttributes() {
      return ["value"];
    }

    attributeChangedCallback(name, _oldVal, newVal) {
      if (name === "value") {
        this._currentValue = newVal || "";
        this.updateTriggerLabel();
        this.updateOptionStates();
      }
    }

    connectedCallback() {
      if (this._rendered) return;
      this._rendered = true;
      this.render();
      this.attachListeners();
      this.updateTriggerLabel();
      this.updateOptionStates();
    }

    disconnectedCallback() {
      document.removeEventListener("click", this.boundCloseOnClickOutside);
      document.removeEventListener("keydown", this.boundCloseOnEscape);
    }

    render() {
      const currentLabel = this.getLabelForValue(this._currentValue);
      this.innerHTML = `
        <div class="collection-sort-dropdown">
          <button type="button"
                  class="collection-sort-dropdown__trigger"
                  aria-haspopup="listbox"
                  aria-expanded="false"
                  aria-label="Sort by"
                  data-js-sort-trigger>

            <div class="collection-sort-dropdown__trigger-icon-container">
              <span class="collection-sort-dropdown__trigger-icon-decorator" aria-hidden="true">
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
                  <path d="M16 16C16 16.2652 15.8946 16.5196 15.7071 16.7071C15.5196 16.8946 15.2652 17 15 17H6C5.73478 17 5.48043 16.8946 5.29289 16.7071C5.10536 16.5196 5 16.2652 5 16C5 15.7348 5.10536 15.4804 5.29289 15.2929C5.48043 15.1054 5.73478 15 6 15H15C15.2652 15 15.5196 15.1054 15.7071 15.2929C15.8946 15.4804 16 15.7348 16 16ZM6 9H23C23.2652 9 23.5196 8.89464 23.7071 8.70711C23.8946 8.51957 24 8.26522 24 8C24 7.73478 23.8946 7.48043 23.7071 7.29289C23.5196 7.10536 23.2652 7 23 7H6C5.73478 7 5.48043 7.10536 5.29289 7.29289C5.10536 7.48043 5 7.73478 5 8C5 8.26522 5.10536 8.51957 5.29289 8.70711C5.48043 8.89464 5.73478 9 6 9ZM13 23H6C5.73478 23 5.48043 23.1054 5.29289 23.2929C5.10536 23.4804 5 23.7348 5 24C5 24.2652 5.10536 24.5196 5.29289 24.7071C5.48043 24.8946 5.73478 25 6 25H13C13.2652 25 13.5196 24.8946 13.7071 24.7071C13.8946 24.5196 14 24.2652 14 24C14 23.7348 13.8946 23.4804 13.7071 23.2929C13.5196 23.1054 13.2652 23 13 23ZM28.7075 20.2925C28.6146 20.1995 28.5043 20.1258 28.3829 20.0754C28.2615 20.0251 28.1314 19.9992 28 19.9992C27.8686 19.9992 27.7385 20.0251 27.6171 20.0754C27.4957 20.1258 27.3854 20.1995 27.2925 20.2925L24 23.5863V14C24 13.7348 23.8946 13.4804 23.7071 13.2929C23.5196 13.1054 23.2652 13 23 13C22.7348 13 22.4804 13.1054 22.2929 13.2929C22.1054 13.4804 22 13.7348 22 14V23.5863L18.7075 20.2925C18.5199 20.1049 18.2654 19.9994 18 19.9994C17.7346 19.9994 17.4801 20.1049 17.2925 20.2925C17.1049 20.4801 16.9994 20.7346 16.9994 21C16.9994 21.2654 17.1049 21.5199 17.2925 21.7075L22.2925 26.7075C22.3854 26.8005 22.4957 26.8742 22.6171 26.9246C22.7385 26.9749 22.8686 27.0008 23 27.0008C23.1314 27.0008 23.2615 26.9749 23.3829 26.9246C23.5043 26.8742 23.6146 26.8005 23.7075 26.7075L28.7075 21.7075C28.8005 21.6146 28.8742 21.5043 28.9246 21.3829C28.9749 21.2615 29.0008 21.1314 29.0008 21C29.0008 20.8686 28.9749 20.7385 28.9246 20.6171C28.8742 20.4957 28.8005 20.3854 28.7075 20.2925Z" fill="#8A93A6"/>
                </svg>
              </span>
              <span class="collection-sort-dropdown__trigger-text body-large text-weight--demi-bold color-neutral-grey-500" data-js-sort-label>${this.escapeHtml(currentLabel)}</span>
            </div>
            <span class="collection-sort-dropdown__trigger-icon" aria-hidden="true">
              <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 26 26" fill="none">
                <path d="M21.7051 10.3274L13.5781 18.4543C13.5026 18.5299 13.413 18.5899 13.3143 18.6308C13.2157 18.6717 13.1099 18.6927 13.0031 18.6927C12.8963 18.6927 12.7906 18.6717 12.6919 18.6308C12.5932 18.5899 12.5036 18.5299 12.4281 18.4543L4.30115 10.3274C4.14866 10.1749 4.06299 9.96804 4.06299 9.75238C4.06299 9.53672 4.14866 9.32989 4.30115 9.1774C4.45365 9.0249 4.66048 8.93923 4.87614 8.93923C5.0918 8.93923 5.29863 9.0249 5.45112 9.1774L13.0031 16.7304L20.5551 9.1774C20.6306 9.10189 20.7203 9.04199 20.8189 9.00113C20.9176 8.96026 21.0233 8.93923 21.1301 8.93923C21.2369 8.93923 21.3426 8.96026 21.4413 9.00113C21.5399 9.04199 21.6296 9.10189 21.7051 9.1774C21.7806 9.2529 21.8405 9.34254 21.8814 9.4412C21.9222 9.53986 21.9433 9.6456 21.9433 9.75238C21.9433 9.85916 21.9222 9.9649 21.8814 10.0636C21.8405 10.1622 21.7806 10.2519 21.7051 10.3274Z" fill="#8A93A6"/>
              </svg>
            </span>
          </button>
          <div class="collection-sort-dropdown__panel"
               role="listbox"
               aria-label="Sort options"
               hidden
               data-js-sort-panel>
            ${SORT_OPTIONS.map(
              (opt) => `
              <button type="button"
                      class="collection-sort-dropdown__option body-large text-weight--demi-bold color-neutral-grey-500 text-left"
                      role="option"
                      data-value="${this.escapeHtml(opt.value)}"
                      ${opt.value === this._currentValue ? 'aria-selected="true"' : ""}>
                ${this.escapeHtml(opt.label)}
              </button>
            `,
            ).join("")}
          </div>
        </div>
      `;
    }

    escapeHtml(str) {
      if (!str) return "";
      const div = document.createElement("div");
      div.textContent = str;
      return div.innerHTML;
    }

    getLabelForValue(value) {
      if (!value) return DEFAULT_LABEL;
      const opt = SORT_OPTIONS.find((o) => o.value === value);
      return opt ? opt.label : DEFAULT_LABEL;
    }

    attachListeners() {
      const trigger = this.querySelector("[data-js-sort-trigger]");
      const panel = this.querySelector("[data-js-sort-panel]");

      trigger.addEventListener("click", (e) => {
        e.stopPropagation();
        this.toggle();
      });

      panel
        .querySelectorAll(".collection-sort-dropdown__option")
        .forEach((btn) => {
          btn.addEventListener("click", (e) => {
            e.stopPropagation();
            const value = btn.getAttribute("data-value");
            this.selectOption(value);
          });
        });

      document.addEventListener("click", this.boundCloseOnClickOutside);
      document.addEventListener("keydown", this.boundCloseOnEscape);
    }

    toggle() {
      this._open = !this._open;
      this.updateOpenState();
    }

    open() {
      this._open = true;
      this.updateOpenState();
    }

    close() {
      this._open = false;
      this.updateOpenState();
    }

    updateOpenState() {
      const trigger = this.querySelector("[data-js-sort-trigger]");
      const panel = this.querySelector("[data-js-sort-panel]");
      if (!trigger || !panel) return;

      trigger.setAttribute("aria-expanded", String(this._open));
      if (this._open) {
        panel.removeAttribute("hidden");
        this.classList.add("collection-sort-dropdown--open");
      } else {
        panel.setAttribute("hidden", "");
        this.classList.remove("collection-sort-dropdown--open");
      }
    }

    updateTriggerLabel() {
      const labelEl = this.querySelector("[data-js-sort-label]");
      if (labelEl) {
        labelEl.textContent = this.getLabelForValue(this._currentValue);
      }
    }

    updateOptionStates() {
      this.querySelectorAll(".collection-sort-dropdown__option").forEach(
        (btn) => {
          const value = btn.getAttribute("data-value");
          btn.setAttribute(
            "aria-selected",
            value === this._currentValue ? "true" : "false",
          );
          btn.classList.toggle(
            "collection-sort-dropdown__option--selected",
            value === this._currentValue,
          );
        },
      );
    }

    selectOption(value) {
      this._currentValue = value;
      this.setAttribute("value", value);
      this.updateTriggerLabel();
      this.updateOptionStates();
      this.close();
      if (value) this.sortProductGrid(value);
    }

    sortProductGrid(sortValue) {
      const grid = document.querySelector(GRID_SELECTOR);
      if (!grid) return;

      const items = Array.from(grid.querySelectorAll(ITEM_SELECTOR));
      if (items.length === 0) return;

      const getTitle = (el) =>
        (el.getAttribute("data-product-title") || "").toLowerCase();
      const getPrice = (el) =>
        parseInt(el.getAttribute("data-product-price") || "0", 10);
      const getCreated = (el) =>
        parseInt(el.getAttribute("data-product-created") || "0", 10);

      const compare = (a, b) => {
        switch (sortValue) {
          case "title-ascending":
            return getTitle(a).localeCompare(getTitle(b));
          case "title-descending":
            return getTitle(b).localeCompare(getTitle(a));
          case "price-ascending":
            return getPrice(a) - getPrice(b);
          case "price-descending":
            return getPrice(b) - getPrice(a);
          case "created-ascending":
            return getCreated(a) - getCreated(b);
          case "created-descending":
            return getCreated(b) - getCreated(a);
          default:
            return 0;
        }
      };

      items.sort(compare);
      items.forEach((item) => grid.appendChild(item));
    }

    closeOnClickOutside(e) {
      if (!this._open) return;
      if (this.contains(e.target)) return;
      this.close();
    }

    closeOnEscape(e) {
      if (e.key === "Escape" && this._open) {
        this.close();
      }
    }
  }

  if (
    typeof customElements !== "undefined" &&
    !customElements.get("collection-sort-dropdown")
  ) {
    customElements.define("collection-sort-dropdown", CollectionSortDropdown);
  }
})();
